import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { MenuItem } from "primereact/menuitem";
import { MobXProviderContext } from "mobx-react";
import { useContext, useEffect, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import ActionMenuButton from "../../components/general/ActionMenuButton";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../constants/RegisterConstants";
import { RouterAddress } from "../../constants/RouterAddress";

type BreadcrumbItem = {
  label: string;
  to?: string;
};

type BreadcrumbRoute = {
  path: string;
  items: BreadcrumbItem[];
};

type ResidentSwitcherOption = {
  residentId: string;
  residentName: string;
  homeId: string;
};

type ResidentSwitcherRoute = {
  path: string;
  featureKey?: string;
};

const getSharedCalendarBreadcrumbItems = (roleType: string | undefined): BreadcrumbItem[] => {
  switch (String(roleType || "").trim()) {
    case ROLE_TYPE_NURSE:
      return [
        { label: "Nurse Home", to: RouterAddress.NURSE_HOME },
        { label: "Shared Calendar" },
      ];
    case ROLE_TYPE_CAREGIVER:
      return [
        { label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME },
        { label: "Shared Calendar" },
      ];
    case ROLE_TYPE_MHP:
      return [
        { label: "MHP Home", to: RouterAddress.MHP_HOME },
        { label: "Shared Calendar" },
      ];
    case ROLE_TYPE_ADMIN:
      return [
        { label: "Home", to: RouterAddress.PROVIDER_HOME },
        { label: "Shared Calendar" },
      ];
    default:
      return [
        { label: "Home", to: RouterAddress.PROVIDER_HOME },
        { label: "Shared Calendar" },
      ];
  }
};

const getHomeBreadcrumbItems = (roleType: string | undefined): BreadcrumbItem[] => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (normalizedRoleType === ROLE_TYPE_CAREGIVER) {
    return [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Care Activity" }];
  }
  if (normalizedRoleType === ROLE_TYPE_NURSE) {
    return [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Care Activity" }];
  }
  if (normalizedRoleType === ROLE_TYPE_MHP) {
    return [{ label: "MHP Home", to: RouterAddress.MHP_HOME }, { label: "Care Activity" }];
  }
  if (normalizedRoleType === "provider_nurse") {
    return [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Care Activity" }];
  }
  return [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Care Activity" }];
};

const getFacilityTasksBreadcrumbItems = (roleType: string | undefined): BreadcrumbItem[] => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (normalizedRoleType === ROLE_TYPE_FACILITY_WORKER) {
    return [{ label: "Facility Worker Home", to: RouterAddress.FACILITY_WORKER_HOME }, { label: "Facility Tasks" }];
  }
  if (normalizedRoleType === ROLE_TYPE_CAREGIVER) {
    return [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Facility Tasks" }];
  }
  if (normalizedRoleType === ROLE_TYPE_NURSE) {
    return [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Facility Tasks" }];
  }
  if (normalizedRoleType === ROLE_TYPE_MHP) {
    return [{ label: "MHP Home", to: RouterAddress.MHP_HOME }, { label: "Facility Tasks" }];
  }
  return [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Facility Tasks" }];
};

const getBillingBreadcrumbItems = (roleType: string | undefined): BreadcrumbItem[] => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (normalizedRoleType === ROLE_TYPE_FACILITY_WORKER) {
    return [{ label: "Facility Worker Home", to: RouterAddress.FACILITY_WORKER_HOME }, { label: "Billing" }];
  }
  return [{ label: "Billing" }];
};

const CARE_ACTIVITY_TYPES = [
  { label: "All activity", value: "all" },
  { label: "eMAR", value: "emar" },
  { label: "ADL", value: "adl" },
  { label: "Progress Notes", value: "daily_note" },
  { label: "Entry Tracker", value: "resident_movement" },
  { label: "Incident Reports", value: "incident" },
  { label: "Behavior", value: "behavior" },
];

const CARE_ACTIVITY_TYPE_OPTIONS = [
  CARE_ACTIVITY_TYPES[0],
  ...CARE_ACTIVITY_TYPES.slice(1).sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" })),
];

const sortCareActivityResidents = (residents: ResidentSwitcherOption[]) => [...residents].sort((left, right) => (
  String(left?.residentName || "").localeCompare(String(right?.residentName || ""), undefined, { sensitivity: "base" })
));

const getLosAngelesDateKey = (value: Date) => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(value);

const getSharedResidentBreadcrumbItems = (roleType: string | undefined, routePath: string | undefined): BreadcrumbItem[] => {
  const normalizedRoleType = String(roleType || "").trim().toLowerCase();
  const homeItem = normalizedRoleType === ROLE_TYPE_MHP
    ? { label: "MHP Home", to: RouterAddress.MHP_HOME }
    : normalizedRoleType === ROLE_TYPE_NURSE
      ? { label: "Nurse Home", to: RouterAddress.NURSE_HOME }
      : { label: "Home", to: RouterAddress.PROVIDER_HOME };

  if (routePath === RouterAddress.SHARED_RESIDENT_PROFILE_PAGE) {
    return [homeItem, { label: "Profile" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Assessment" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_NCP_PAGE || routePath === RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Care Plan" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Search Care Plan" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_EMAR_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "eMAR" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_ADL_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "ADL Tracking" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Daily Behavior Log" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Incident Reports" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Progress Notes" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_ENTRY_TRACKER_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Entry Tracker" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Delegation" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE) {
    return [
      homeItem,
      { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE },
      { label: "Delegation", to: RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE },
      { label: "Delegation Form" },
    ];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Foot & Nail Care" }];
  }
  if (routePath === RouterAddress.SHARED_RESIDENT_BINDER_PAGE) {
    return [homeItem, { label: "Profile", to: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE }, { label: "Binder" }];
  }

  return [homeItem];
};

const BREADCRUMB_ROUTES: BreadcrumbRoute[] = [
  { path: RouterAddress.PROVIDER_HOME, items: [{ label: "Home" }] },
  { path: RouterAddress.FACILITY_ADMIN_DASHBOARD, items: [{ label: "Home" }] },
  { path: RouterAddress.CAREGIVER_HOME, items: [{ label: "Caregiver Home" }] },
  { path: RouterAddress.NURSE_HOME, items: [{ label: "Nurse Home" }] },
  { path: RouterAddress.MHP_HOME, items: [{ label: "MHP Home" }] },
  { path: RouterAddress.FAMILY_MEMBER_HOME, items: [{ label: "Family Home" }] },
  { path: RouterAddress.PROVIDER_NURSE_HOME, items: [{ label: "Nurse Home" }] },
  { path: RouterAddress.VENDOR_HOME, items: [{ label: "Vendor Home" }] },
  { path: RouterAddress.AFH_DETAILS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Home Details" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_ASSESSMENT_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Assessment Form" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_NCP_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Care Plan" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_BINDER_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Binder" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Delegation Forms" }] },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Foot & Nail Care" }] },
  { path: RouterAddress.PROVIDER_NURSE_DELEGATION_FORM_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.PROVIDER_NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE }, { label: "Delegation Forms", to: RouterAddress.PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE }, { label: "Delegation Form" }] },
  { path: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile" }] },
  { path: RouterAddress.NURSE_RESIDENT_ASSESSMENT_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Assessment Form" }] },
  { path: RouterAddress.NURSE_RESIDENT_NCP_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Care Plan" }] },
  { path: RouterAddress.NURSE_RESIDENT_BINDER_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Binder" }] },
  { path: RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Delegation Forms" }] },
  { path: RouterAddress.NURSE_RESIDENT_PODIATRIST_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Foot & Nail Care" }] },
  { path: RouterAddress.NURSE_DELEGATION_REFERRAL_FORM_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Delegation Forms", to: RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE }, { label: "01-212 Referral Form" }] },
  { path: RouterAddress.NURSE_DELEGATION_FORM_PAGE, items: [{ label: "Nurse Home", to: RouterAddress.NURSE_HOME }, { label: "Resident Profile", to: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE }, { label: "Delegation Forms", to: RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE }, { label: "Delegation Form" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_PROFILE_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Assessment" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Assessment" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_EDITOR_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Assessment" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_DELEGATION_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Delegation Forms" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_DELEGATION_FORM_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Delegation Forms", to: RouterAddress.PROVIDER_RESIDENT_DELEGATION_PAGE }, { label: "Delegation Form" }] },
  { path: RouterAddress.MANAGE_WEBSITE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Manage Website" }] },
  { path: RouterAddress.HOME_BINDER_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Binder" }] },
  { path: RouterAddress.HOME_INTAKE_RECEIVED_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Screening" }] },
  { path: RouterAddress.HOME_DOCUMENTS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Documents" }] },
  { path: RouterAddress.HOME_STAFF_MANAGEMENT_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Staff Management" }] },
  { path: RouterAddress.HOME_CARE_ACTIVITY_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Care Activity" }] },
  { path: RouterAddress.HOME_FACILITY_TASKS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Facility Tasks" }] },
  { path: RouterAddress.CAREGIVER_HOME_FACILITY_TASKS_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Facility Tasks" }] },
  { path: RouterAddress.FACILITY_WORKER_HOME_FACILITY_TASKS_PAGE, items: [{ label: "Facility Worker Home", to: RouterAddress.FACILITY_WORKER_HOME }, { label: "Facility Tasks" }] },
  { path: RouterAddress.SHARED_HOME_FACILITY_TASKS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Facility Tasks" }] },
  { path: RouterAddress.RESIDENT_DETAILS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details" }] },
  { path: RouterAddress.RESIDENT_NCP_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Care Plan" }] },
  { path: RouterAddress.RESIDENT_ESF_CARE_PLAN_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "ESF Care Plan" }] },
  { path: RouterAddress.RESIDENT_BINDER_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Binder" }] },
  { path: RouterAddress.RESIDENT_DOCUMENTS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Documents" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_EMAR_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "eMAR" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_ADL_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "ADL Tracking" }] },
  { path: RouterAddress.CAREGIVER_DETAILS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Caregiver Details" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_CARE_PLAN_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Search Care Plan" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Daily Behavior Log" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_INCIDENT_REPORT_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Incident Reports" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_DAILY_NOTES_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details", to: RouterAddress.RESIDENT_DETAILS_PAGE }, { label: "Progress Notes" }] },
  { path: RouterAddress.PROVIDER_RESIDENT_ENTRY_TRACKER_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Resident Details" }, { label: "Entry Tracker" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_CARE_PLAN_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Resident Details" }, { label: "Search Care Plan" }] },
  { path: RouterAddress.CAREGIVER_ATTENDANCE_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Time & Pay" }] },
  { path: RouterAddress.FACILITY_WORKER_ATTENDANCE_PAGE, items: [{ label: "Facility Worker Home", to: RouterAddress.FACILITY_WORKER_HOME }, { label: "Time & Pay" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_EMAR_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "eMAR" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_ADL_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "ADL Tracking" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Resident Details" }, { label: "Daily Behavior Log" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_INCIDENT_REPORT_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Resident Details" }, { label: "Incident Reports" }] },
  { path: RouterAddress.CAREGIVER_RESIDENT_DAILY_NOTES_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Resident Details" }, { label: "Progress Notes" }] },
  { path: RouterAddress.CAREGIVER_JOB_BINDER_PAGE, items: [{ label: "Caregiver Home", to: RouterAddress.CAREGIVER_HOME }, { label: "Binder" }] },
  { path: RouterAddress.FACILITY_WORKER_JOB_BINDER_PAGE, items: [{ label: "Facility Worker Home", to: RouterAddress.FACILITY_WORKER_HOME }, { label: "Binder" }] },
  { path: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE, items: [{ label: "Profile" }] },
  { path: RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE, items: [{ label: "Profile" }, { label: "Assessment" }] },
  { path: RouterAddress.SHARED_RESIDENT_NCP_PAGE, items: [{ label: "Profile" }, { label: "Care Plan" }] },
  { path: RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE, items: [{ label: "Profile" }, { label: "Care Plan" }] },
  { path: RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE, items: [{ label: "Profile" }, { label: "Search Care Plan" }] },
  { path: RouterAddress.SHARED_RESIDENT_EMAR_PAGE, items: [{ label: "Profile" }, { label: "eMAR" }] },
  { path: RouterAddress.SHARED_RESIDENT_ADL_PAGE, items: [{ label: "Profile" }, { label: "ADL Tracking" }] },
  { path: RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, items: [{ label: "Profile" }, { label: "Daily Behavior Log" }] },
  { path: RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE, items: [{ label: "Profile" }, { label: "Incident Reports" }] },
  { path: RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE, items: [{ label: "Profile" }, { label: "Progress Notes" }] },
  { path: RouterAddress.SHARED_RESIDENT_ENTRY_TRACKER_PAGE, items: [{ label: "Profile" }, { label: "Entry Tracker" }] },
  { path: RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE, items: [{ label: "Profile" }, { label: "Delegation" }] },
  { path: RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE, items: [{ label: "Profile" }, { label: "Delegation" }, { label: "Delegation Form" }] },
  { path: RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE, items: [{ label: "Profile" }, { label: "Foot & Nail Care" }] },
  { path: RouterAddress.SHARED_RESIDENT_BINDER_PAGE, items: [{ label: "Profile" }, { label: "Binder" }] },
  { path: RouterAddress.CAREGIVER_DOCUMENTS_PAGE, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Caregiver Details" }, { label: "Documents" }] },
  { path: RouterAddress.USER_DOCUMENTS_PAGE, items: [{ label: "Documents" }] },
  { path: RouterAddress.USER_REMINDER_PAGE, items: [{ label: "Reminders" }] },
  { path: RouterAddress.SHARED_CALENDAR, items: [{ label: "Home", to: RouterAddress.PROVIDER_HOME }, { label: "Shared Calendar" }] },
  { path: RouterAddress.BILLING, items: [{ label: "Billing" }] },
  { path: RouterAddress.CONNECTIONS_PAGE, items: [{ label: "Connections" }] },
  { path: RouterAddress.PROFILE, items: [{ label: "Profile" }] },
  { path: RouterAddress.SUPPORT, items: [{ label: "Support" }] },
  { path: RouterAddress.ADMIN_PAGE, items: [{ label: "Admin" }] },
  { path: RouterAddress.AI_COMPANION, items: [{ label: "AI Companion" }] },
  { path: RouterAddress.USER_REVIEWS_PAGE, items: [{ label: "Reviews" }] },
  { path: RouterAddress.FIND_CAREGIVERS, items: [{ label: "Explore" }, { label: "Find Caregivers" }] },
  { path: RouterAddress.SEARCH_JOB, items: [{ label: "Explore" }, { label: "Find Jobs" }] },
  { path: RouterAddress.LISTINGS, items: [{ label: "Explore" }, { label: "Facilities Listings" }] },
  { path: RouterAddress.PROVIDER_DIRECTORY_MAP, items: [{ label: "Explore" }, { label: "Provider Map" }] },
];

const RESIDENT_SWITCHER_ROUTES: ResidentSwitcherRoute[] = [
  { path: RouterAddress.RESIDENT_DETAILS_PAGE },
  { path: RouterAddress.RESIDENT_NCP_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.RESIDENT_ESF_CARE_PLAN_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.RESIDENT_BINDER_PAGE, featureKey: "binder" },
  { path: RouterAddress.RESIDENT_DOCUMENTS_PAGE },
  { path: RouterAddress.PROVIDER_RESIDENT_PROFILE_PAGE },
  { path: RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_PAGE, featureKey: "assessment" },
  { path: RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_EDITOR_PAGE, featureKey: "assessment" },
  { path: RouterAddress.PROVIDER_RESIDENT_DELEGATION_PAGE, featureKey: "delegation" },
  { path: RouterAddress.PROVIDER_RESIDENT_DELEGATION_FORM_PAGE, featureKey: "delegation" },
  { path: RouterAddress.PROVIDER_RESIDENT_CARE_PLAN_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.PROVIDER_RESIDENT_EMAR_PAGE, featureKey: "emar" },
  { path: RouterAddress.PROVIDER_RESIDENT_ADL_PAGE, featureKey: "adl" },
  { path: RouterAddress.PROVIDER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, featureKey: "behaviorLog" },
  { path: RouterAddress.PROVIDER_RESIDENT_INCIDENT_REPORT_PAGE, featureKey: "incidentReports" },
  { path: RouterAddress.PROVIDER_RESIDENT_DAILY_NOTES_PAGE, featureKey: "dailyNotes" },
  { path: RouterAddress.PROVIDER_RESIDENT_ENTRY_TRACKER_PAGE, featureKey: "dailyNotes" },
  { path: RouterAddress.CAREGIVER_RESIDENT_CARE_PLAN_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.CAREGIVER_RESIDENT_EMAR_PAGE, featureKey: "emar" },
  { path: RouterAddress.CAREGIVER_RESIDENT_ADL_PAGE, featureKey: "adl" },
  { path: RouterAddress.CAREGIVER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, featureKey: "behaviorLog" },
  { path: RouterAddress.CAREGIVER_RESIDENT_INCIDENT_REPORT_PAGE, featureKey: "incidentReports" },
  { path: RouterAddress.CAREGIVER_RESIDENT_DAILY_NOTES_PAGE, featureKey: "dailyNotes" },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_ASSESSMENT_PAGE, featureKey: "assessment" },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_NCP_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE, featureKey: "delegation" },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE },
  { path: RouterAddress.PROVIDER_NURSE_RESIDENT_BINDER_PAGE, featureKey: "binder" },
  { path: RouterAddress.PROVIDER_NURSE_DELEGATION_FORM_PAGE, featureKey: "delegation" },
  { path: RouterAddress.NURSE_RESIDENT_PROFILE_PAGE },
  { path: RouterAddress.NURSE_RESIDENT_ASSESSMENT_PAGE, featureKey: "assessment" },
  { path: RouterAddress.NURSE_RESIDENT_NCP_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.NURSE_RESIDENT_BINDER_PAGE, featureKey: "binder" },
  { path: RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE, featureKey: "delegation" },
  { path: RouterAddress.NURSE_RESIDENT_PODIATRIST_PAGE },
  { path: RouterAddress.NURSE_DELEGATION_REFERRAL_FORM_PAGE, featureKey: "delegation" },
  { path: RouterAddress.NURSE_DELEGATION_FORM_PAGE, featureKey: "delegation" },
  { path: RouterAddress.SHARED_RESIDENT_PROFILE_PAGE },
  { path: RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE, featureKey: "assessment" },
  { path: RouterAddress.SHARED_RESIDENT_NCP_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE, featureKey: "carePlan" },
  { path: RouterAddress.SHARED_RESIDENT_EMAR_PAGE, featureKey: "emar" },
  { path: RouterAddress.SHARED_RESIDENT_ADL_PAGE, featureKey: "adl" },
  { path: RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE, featureKey: "behaviorLog" },
  { path: RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE, featureKey: "incidentReports" },
  { path: RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE, featureKey: "dailyNotes" },
  { path: RouterAddress.SHARED_RESIDENT_ENTRY_TRACKER_PAGE, featureKey: "dailyNotes" },
  { path: RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE, featureKey: "delegation" },
  { path: RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE, featureKey: "delegation" },
  { path: RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE },
  { path: RouterAddress.SHARED_RESIDENT_BINDER_PAGE, featureKey: "binder" },
];

const resolveBreadcrumbTarget = (targetPath: string, params: Record<string, string | undefined>) => {
  return Object.entries(params).reduce((resolvedPath, [key, value]) => {
    return resolvedPath.replace(`:${key}`, value || "");
  }, targetPath);
};

const AppBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const matchedRoute = BREADCRUMB_ROUTES.find((route) =>
    matchPath({ path: route.path, end: true }, location.pathname),
  );

  const routeMatch = matchedRoute
    ? matchPath({ path: matchedRoute.path, end: true }, location.pathname)
    : null;
  const searchParams = new URLSearchParams(location.search);
  const residentId = routeMatch?.params?.residentId;
  const residentSwitcherRoute = RESIDENT_SWITCHER_ROUTES.find((route) => route.path === matchedRoute?.path);
  const isResidentSwitcherRoute = Boolean(residentSwitcherRoute && residentId);
  const [residentSwitcherResidents, setResidentSwitcherResidents] = useState<ResidentSwitcherOption[]>([]);
  const [residentSwitcherLoading, setResidentSwitcherLoading] = useState(false);
  const [careActivityResidents, setCareActivityResidents] = useState<ResidentSwitcherOption[]>([]);
  const [resolvedResidentHomeId, setResolvedResidentHomeId] = useState<string>("");
  const breadcrumbParams = {
    ...(routeMatch?.params || {}),
    homeId: routeMatch?.params?.homeId || searchParams.get("homeId") || resolvedResidentHomeId || undefined,
  };

  const isSharedResidentRoute = [
    RouterAddress.SHARED_RESIDENT_PROFILE_PAGE,
    RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE,
    RouterAddress.SHARED_RESIDENT_NCP_PAGE,
    RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE,
    RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE,
    RouterAddress.SHARED_RESIDENT_EMAR_PAGE,
    RouterAddress.SHARED_RESIDENT_ADL_PAGE,
    RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE,
    RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE,
    RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE,
    RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE,
    RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE,
    RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE,
    RouterAddress.SHARED_RESIDENT_BINDER_PAGE,
  ].includes(String(matchedRoute?.path || ""));

  const isFacilityTasksRoute = [
    RouterAddress.HOME_FACILITY_TASKS_PAGE,
    RouterAddress.CAREGIVER_HOME_FACILITY_TASKS_PAGE,
    RouterAddress.FACILITY_WORKER_HOME_FACILITY_TASKS_PAGE,
    RouterAddress.SHARED_HOME_FACILITY_TASKS_PAGE,
  ].includes(String(matchedRoute?.path || ""));

  const baseItems = matchedRoute?.path === RouterAddress.SHARED_CALENDAR
    ? getSharedCalendarBreadcrumbItems(authStore?.getUserRoleType?.())
    : matchedRoute?.path === RouterAddress.BILLING
      ? getBillingBreadcrumbItems(authStore?.getUserRoleType?.())
    : isSharedResidentRoute
      ? getSharedResidentBreadcrumbItems(authStore?.getUserRoleType?.(), matchedRoute?.path)
      : (matchedRoute?.path === RouterAddress.HOME_CARE_ACTIVITY_PAGE || matchedRoute?.path === RouterAddress.SHARED_HOME_CARE_ACTIVITY_PAGE)
        ? getHomeBreadcrumbItems(authStore?.getUserRoleType?.())
      : isFacilityTasksRoute
        ? getFacilityTasksBreadcrumbItems(authStore?.getUserRoleType?.())
      : (matchedRoute?.items ?? []);

  const items = baseItems;
  const isResidentDetailsRoute = matchedRoute?.path === RouterAddress.RESIDENT_DETAILS_PAGE;
  const isProviderResidentEmarRoute = matchedRoute?.path === RouterAddress.PROVIDER_RESIDENT_EMAR_PAGE;
  const isProviderResidentAdlRoute = matchedRoute?.path === RouterAddress.PROVIDER_RESIDENT_ADL_PAGE;
  const isCaregiverResidentEmarRoute = matchedRoute?.path === RouterAddress.CAREGIVER_RESIDENT_EMAR_PAGE;
  const isProviderNurseResidentPodiatristRoute = matchedRoute?.path === RouterAddress.PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE;
  const isNurseResidentPodiatristRoute = matchedRoute?.path === RouterAddress.NURSE_RESIDENT_PODIATRIST_PAGE;
  const isCaregiverAttendanceRoute = matchedRoute?.path === RouterAddress.CAREGIVER_ATTENDANCE_PAGE
    || matchedRoute?.path === RouterAddress.FACILITY_WORKER_ATTENDANCE_PAGE;
  const isSharedCalendarRoute = matchedRoute?.path === RouterAddress.SHARED_CALENDAR;
  const isCareActivityRoute = matchedRoute?.path === RouterAddress.HOME_CARE_ACTIVITY_PAGE
    || matchedRoute?.path === RouterAddress.SHARED_HOME_CARE_ACTIVITY_PAGE;
  const isHomeStaffManagementRoute = matchedRoute?.path === RouterAddress.HOME_STAFF_MANAGEMENT_PAGE;
  const isHomeIntakeReceivedRoute = matchedRoute?.path === RouterAddress.HOME_INTAKE_RECEIVED_PAGE;
  const isProviderHomeBinderRoute = matchedRoute?.path === RouterAddress.HOME_BINDER_PAGE;
  const isProviderResidentBinderRoute = matchedRoute?.path === RouterAddress.RESIDENT_BINDER_PAGE;
  const isProviderNurseResidentBinderRoute = matchedRoute?.path === RouterAddress.PROVIDER_NURSE_RESIDENT_BINDER_PAGE;
  const isNurseResidentBinderRoute = matchedRoute?.path === RouterAddress.NURSE_RESIDENT_BINDER_PAGE;
  const isBinderRoute = isProviderHomeBinderRoute || isProviderResidentBinderRoute || isProviderNurseResidentBinderRoute || isNurseResidentBinderRoute;
  const residentHomeId = breadcrumbParams.homeId;
  const careActivityHomeId = String(breadcrumbParams.homeId || "").trim();
  const careActivitySearchParams = new URLSearchParams(location.search);
  const careActivityResidentId = careActivitySearchParams.get("residentId") || "";
  const careActivityType = careActivitySearchParams.get("activityType") || "all";
  const careActivityDateKey = careActivitySearchParams.get("dateKey") || "";
  const isAdminUser = !!authStore?.isAdmin?.();
  const currentRoleType = String(authStore?.getUserRoleType?.() || "").trim().toLowerCase();
  const [staffSelectionCount, setStaffSelectionCount] = useState(0);
  const [staffArchived, setStaffArchived] = useState(false);
  useEffect(() => {
    if (!isHomeStaffManagementRoute) {
      setStaffSelectionCount(0);
      setStaffArchived(false);
      return undefined;
    }
    const handleStaffSelectionChanged = (event: Event) => {
      const count = (event as CustomEvent<{ count?: number }>).detail?.count;
      setStaffSelectionCount(Number.isFinite(count) ? Number(count) : 0);
    };
    const handleStaffViewChanged = (event: Event) => setStaffArchived(Boolean((event as CustomEvent<{ archived?: boolean }>).detail?.archived));
    window.addEventListener("staff-management:selection-changed", handleStaffSelectionChanged);
    window.addEventListener("staff-management:view-changed", handleStaffViewChanged);
    return () => {
      window.removeEventListener("staff-management:selection-changed", handleStaffSelectionChanged);
      window.removeEventListener("staff-management:view-changed", handleStaffViewChanged);
    };
  }, [isHomeStaffManagementRoute]);
  const canManageFacilityTasks = isAdminUser
    || currentRoleType === ROLE_TYPE_PROVIDER
    || (matchedRoute?.path === RouterAddress.SHARED_HOME_FACILITY_TASKS_PAGE && (currentRoleType === ROLE_TYPE_NURSE || currentRoleType === ROLE_TYPE_MHP));
  const [hasFacilityTaskItems, setHasFacilityTaskItems] = useState(false);
  useEffect(() => {
    if (!isFacilityTasksRoute) {
      setHasFacilityTaskItems(false);
      return undefined;
    }
    setHasFacilityTaskItems(false);
    const handleTaskAvailability = (event: Event) => {
      const detail = (event as CustomEvent<{ count?: number }>).detail;
      const count = Number(detail?.count || 0);
      setHasFacilityTaskItems(count > 0);
    };
    window.addEventListener("facility-tasks:availability", handleTaskAvailability);
    return () => window.removeEventListener("facility-tasks:availability", handleTaskAvailability);
  }, [isFacilityTasksRoute, location.pathname]);
  const [showResidentEmarReportAction, setShowResidentEmarReportAction] = useState(true);
  const [podiatristActionLoading, setPodiatristActionLoading] = useState<Record<string, boolean>>({});
  const residentEmarActionItems: MenuItem[] = isProviderResidentEmarRoute
    ? [
      {
        label: "Add New Medication / Vital Tracker",
        icon: "pi pi-plus",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-emar:open-add-item-dialog"));
          }
        },
      },
      {
        label: "Add Medications from Care Plan",
        icon: "pi pi-download",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-emar:import-care-plan"));
          }
        },
      },
      {
        label: "Add from MAR Sheet/Medication List",
        icon: "pi pi-upload",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-emar:import-mar-sheet"));
          }
        },
      },
    ]
    : [];
  const residentAdlActionItems: MenuItem[] = isProviderResidentAdlRoute
    ? [
      {
        label: "Generate Tasks From Care Plan",
        icon: "pi pi-sparkles",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-adl:open-generate-dialog"));
          }
        },
      },
      {
        label: "Add New Item",
        icon: "pi pi-plus",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-adl:open-custom-dialog"));
          }
        },
      },
      {
        label: "Search Care Plan",
        icon: "pi pi-search",
        command: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("resident-adl:open-care-plan"));
          }
        },
      },
    ]
    : [];

  useEffect(() => {
    const handlePodiatristActionLoading = (event: Event) => {
      const customEvent = event as CustomEvent<{ action?: string; loading?: boolean }>;
      const action = String(customEvent.detail?.action || "").trim();
      if (!action) {
        return;
      }
      setPodiatristActionLoading((current) => ({
        ...current,
        [action]: customEvent.detail?.loading === true,
      }));
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resident-podiatrist:action-loading", handlePodiatristActionLoading as EventListener);
      return () => {
        window.removeEventListener("resident-podiatrist:action-loading", handlePodiatristActionLoading as EventListener);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    const handleReportVisibility = (event: Event) => {
      const detail = (event as CustomEvent<{ visible?: boolean }>)?.detail;
      setShowResidentEmarReportAction(detail?.visible !== false);
    };

    window.addEventListener("resident-emar:set-report-visibility", handleReportVisibility as EventListener);
    return () => {
      window.removeEventListener("resident-emar:set-report-visibility", handleReportVisibility as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isResidentSwitcherRoute || !residentId) {
      setResidentSwitcherResidents([]);
      setResidentSwitcherLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadResidentSwitcherResidents = async () => {
      setResidentSwitcherLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentSwitcherResidents(
          String(residentId),
          residentSwitcherRoute?.featureKey,
        );
        const residents = Array.isArray(response)
          ? response
          : (Array.isArray(response?.residents) ? response.residents : []);
        if (!cancelled) {
          setResidentSwitcherResidents(residents.filter((resident: any) => resident?.residentId && resident?.residentName));
        }
      } catch {
        if (!cancelled) {
          setResidentSwitcherResidents([]);
        }
      } finally {
        if (!cancelled) {
          setResidentSwitcherLoading(false);
        }
      }
    };

    void loadResidentSwitcherResidents();
    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, isResidentSwitcherRoute, residentId, residentSwitcherRoute?.featureKey]);

  const needsResidentHomeLookup = Boolean(
    residentId
    && items.some((item) => item.to === RouterAddress.RESIDENT_DETAILS_PAGE)
    && !breadcrumbParams.homeId
  );

  useEffect(() => {

    if (!needsResidentHomeLookup) {
      if (resolvedResidentHomeId) {
        setResolvedResidentHomeId("");
      }
      return;
    }

    let cancelled = false;
    const loadResidentHomeId = async () => {
      try {
        await backendAPIStore.init();
        const residentDetails = await backendAPIStore.getResidentDetails(String(residentId || ""));
        if (!cancelled) {
          setResolvedResidentHomeId(String(residentDetails?.homeId || "").trim());
        }
      } catch {
        if (!cancelled) {
          setResolvedResidentHomeId("");
        }
      }
    };

    void loadResidentHomeId();
    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, breadcrumbParams.homeId, needsResidentHomeLookup, residentId]);

  useEffect(() => {
    if (!isCareActivityRoute || !careActivityHomeId) {
      setCareActivityResidents([]);
      return;
    }

    let cancelled = false;
    const loadCareActivityResidents = async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getFacilityCareActivity(careActivityHomeId, { limit: 1 });
        if (!cancelled) {
          setCareActivityResidents(sortCareActivityResidents(Array.isArray(response?.residents) ? response.residents : []));
        }
      } catch {
        if (!cancelled) {
          setCareActivityResidents([]);
        }
      }
    };

    void loadCareActivityResidents();
    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, careActivityHomeId, isCareActivityRoute]);

  const updateCareActivityFilter = (key: string, value: string) => {
    const nextSearchParams = new URLSearchParams(location.search);
    if (value) {
      nextSearchParams.set(key, value);
    } else {
      nextSearchParams.delete(key);
    }
    const nextSearch = nextSearchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
  };

  const handleResidentSwitcherChange = (nextResident: ResidentSwitcherOption | null) => {
    if (!nextResident || !residentSwitcherRoute || !routeMatch || nextResident.residentId === residentId) {
      return;
    }

    const targetPath = resolveBreadcrumbTarget(residentSwitcherRoute.path, {
      ...(routeMatch.params || {}),
      homeId: nextResident.homeId,
      residentId: nextResident.residentId,
    });
    const nextSearchParams = new URLSearchParams(location.search);
    if (!residentSwitcherRoute.path.includes(":homeId") && residentSwitcherRoute.path.startsWith("/provider/resident/")) {
      nextSearchParams.set("homeId", nextResident.homeId);
    }
    const nextQuery = nextSearchParams.toString();
    navigate(`${targetPath}${nextQuery ? `?${nextQuery}` : ""}`);
  };

  const selectedResidentSwitcherResident = residentSwitcherResidents.find(
    (resident) => resident.residentId === residentId,
  ) || null;

  if (items.length <= 1) {
    return null;
  }

  return (
    <div className="app-breadcrumbs-shell">
      <div className="app-header-frame">
          <div className={`app-breadcrumbs-row ${isResidentDetailsRoute || isResidentSwitcherRoute || isProviderResidentEmarRoute || isProviderResidentAdlRoute || isCaregiverResidentEmarRoute || isProviderNurseResidentPodiatristRoute || isNurseResidentPodiatristRoute || isCaregiverAttendanceRoute || isSharedCalendarRoute || isCareActivityRoute || isFacilityTasksRoute || isBinderRoute || isHomeStaffManagementRoute || isHomeIntakeReceivedRoute ? "app-breadcrumbs-row-with-actions" : ""}`.trim()}>
          <nav className="app-breadcrumbs" aria-label="Breadcrumb">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;

              return (
                <span key={`${item.label}-${index}`} className="app-breadcrumbs-item">
                  {item.to && !isLast ? (
                    <button
                      type="button"
                      className="app-breadcrumbs-link"
                      onClick={() => navigate(resolveBreadcrumbTarget(item.to!, breadcrumbParams))}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={`app-breadcrumbs-label ${isLast ? "app-breadcrumbs-current" : ""}`.trim()}>
                      {item.label}
                    </span>
                  )}
                  {!isLast ? <span className="app-breadcrumbs-separator">/</span> : null}
                </span>
              );
            })}
          </nav>
          {isResidentSwitcherRoute && residentSwitcherResidents.length > 1 && selectedResidentSwitcherResident ? (
            <div className="app-breadcrumbs-resident-switcher">
              <span className="app-breadcrumbs-resident-switcher-label">Switch resident</span>
              <Dropdown
                value={selectedResidentSwitcherResident}
                options={residentSwitcherResidents}
                optionLabel="residentName"
                onChange={(event) => handleResidentSwitcherChange(event.value as ResidentSwitcherOption | null)}
                placeholder="Select resident"
                aria-label="Switch resident"
                disabled={residentSwitcherLoading}
                className="app-breadcrumbs-resident-switcher-control"
              />
            </div>
          ) : null}
          {isCaregiverAttendanceRoute ? (
            <div className="app-breadcrumbs-actions">
              <Button
                icon="pi pi-question-circle"
                outlined
                rounded
                label="Help Guide"
                severity="warning"
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("caregiver-time-pay:open-help-guide"));
                  }
                }}
              />
            </div>
          ) : null}
          {isSharedCalendarRoute ? (
            <div className="app-breadcrumbs-actions">
              <Button
                icon="pi pi-plus"
                rounded
                label="New Event"
                severity="warning"
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("shared-calendar:request-create"));
                  }
                }}
              />
            </div>
          ) : null}
          {isCareActivityRoute ? (
            <div className="app-breadcrumbs-actions care-activity-breadcrumb-filters">
              <Button
                type="button"
                icon="pi pi-refresh"
                aria-label="Refresh Care Activity"
                tooltip="Refresh Care Activity"
                tooltipOptions={{ position: "top" }}
                outlined
                rounded
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("care-activity:refresh"));
                  }
                }}
              />
              <Dropdown
                value={careActivityResidentId}
                options={[{ residentId: "", residentName: "All residents" }, ...sortCareActivityResidents(careActivityResidents)]}
                optionLabel="residentName"
                optionValue="residentId"
                onChange={(event) => updateCareActivityFilter("residentId", String(event.value || ""))}
                placeholder="Resident"
                aria-label="Filter by resident"
                className="app-breadcrumbs-resident-switcher-control"
              />
              <Dropdown
                value={careActivityType}
                options={CARE_ACTIVITY_TYPE_OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={(event) => updateCareActivityFilter("activityType", String(event.value || "all"))}
                placeholder="Activity type"
                aria-label="Filter by activity type"
                className="app-breadcrumbs-resident-switcher-control"
              />
              <Calendar
                value={careActivityDateKey ? new Date(`${careActivityDateKey}T12:00:00`) : null}
                onChange={(event) => updateCareActivityFilter("dateKey", event.value ? getLosAngelesDateKey(event.value as Date) : "")}
                placeholder="Filter by date"
                dateFormat="yy-mm-dd"
                showIcon
                aria-label="Filter by date"
                className="app-breadcrumbs-resident-switcher-control"
              />
              <span className="care-activity-date-filter-clear">
                {careActivityDateKey ? (
                  <Button
                    type="button"
                    icon="pi pi-times"
                    aria-label="Clear date filter"
                    tooltip="Clear date filter"
                    tooltipOptions={{ position: "top" }}
                    text
                    rounded
                    onClick={() => updateCareActivityFilter("dateKey", "")}
                  />
                ) : null}
              </span>
            </div>
          ) : null}
          {isHomeStaffManagementRoute && !staffArchived ? (
            <div className="app-breadcrumbs-actions">
              <Button type="button" label="Add New Staff" icon="pi pi-user-plus" severity="warning" rounded size="small" onClick={() => window.dispatchEvent(new CustomEvent("staff-management:add-staff"))} />
              <Button type="button" label="New Staff Hiring Form" icon="pi pi-send" severity="warning" outlined rounded size="small" onClick={() => window.dispatchEvent(new CustomEvent("staff-management:new-hiring-intake"))} />
              {staffSelectionCount > 1 ? (
                <>
                  <Button type="button" label="Bulk Edit Shift" icon="pi pi-clock" severity="warning" outlined rounded size="small" onClick={() => window.dispatchEvent(new CustomEvent("staff-management:bulk-shift"))} />
                  <Button type="button" label="Bulk Edit Access" icon="pi pi-users" severity="warning" outlined rounded size="small" onClick={() => window.dispatchEvent(new CustomEvent("staff-management:bulk-access"))} />
                </>
              ) : null}
            </div>
          ) : null}
          {isHomeIntakeReceivedRoute ? (
            <div className="app-breadcrumbs-actions">
              <Button
                type="button"
                label="Manage Screening Form"
                icon="pi pi-file-edit"
                severity="warning"
                rounded
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("intake-requests:manage-form"));
                  }
                }}
              />
            </div>
          ) : null}
          {isFacilityTasksRoute && currentRoleType === ROLE_TYPE_CAREGIVER && hasFacilityTaskItems ? (
            <div className="app-breadcrumbs-actions">
              <Button
                type="button"
                icon="pi pi-file-export"
                label="Export Activity"
                severity="warning"
                outlined
                rounded
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("facility-tasks:open-export"));
                }}
              />
            </div>
          ) : null}
          {isFacilityTasksRoute && canManageFacilityTasks ? (
            <div className="app-breadcrumbs-actions">
              <Button
                type="button"
                icon="pi pi-sparkles"
                label="Auto-Generate Tasks"
                severity="help"
                outlined
                rounded
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("facility-tasks:open-auto-generate"));
                }}
              />
              <Button
                type="button"
                icon="pi pi-plus"
                label="Add Task"
                severity="warning"
                rounded
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("facility-tasks:open-add"));
                }}
              />
              {hasFacilityTaskItems ? <Button
                  type="button"
                  icon="pi pi-file-export"
                  label="Export Activity"
                  severity="warning"
                  outlined
                  rounded
                  size="small"
                  onClick={() => {
                    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("facility-tasks:open-export"));
                  }}
                /> : null}
            </div>
          ) : null}
          {isResidentDetailsRoute && residentHomeId && residentId ? (
            <div className="app-breadcrumbs-actions">
              <Button
                icon="pi pi-folder-open"
                outlined
                rounded
                label="Binder"
                severity="warning"
                size="small"
                onClick={() => navigate(RouterAddress.getResidentBinderPagePath(residentHomeId, residentId))}
              />
              {isAdminUser ? (
                <Button
                  outlined
                  rounded
                  label="Delete"
                  severity="danger"
                  size="small"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(new CustomEvent("resident-details:request-delete"));
                    }
                  }}
                />
              ) : null}
            </div>
          ) : null}
          {(isProviderResidentEmarRoute || isCaregiverResidentEmarRoute) && residentId ? (
            <div className="app-breadcrumbs-actions">
              {isProviderResidentEmarRoute ? (
                <ActionMenuButton
                  label="Add"
                  model={residentEmarActionItems}
                  icon="pi pi-chevron-down"
                  severity="warning"
                  rounded
                  className="p-button-sm"
                />
              ) : null}
              {showResidentEmarReportAction ? (
                <Button
                  icon="pi pi-file-export"
                  rounded
                  label="Generate MAR Report"
                  severity="warning"
                  size="small"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(new CustomEvent("resident-emar:request-generate-report"));
                    }
                  }}
                />
              ) : null}
            </div>
          ) : null}
          {(isProviderNurseResidentPodiatristRoute || isNurseResidentPodiatristRoute) && residentId ? (
            <div className="app-breadcrumbs-actions">
              <ActionMenuButton
                label="Start New Tracker"
                model={[
                  {
                    label: "New Blank Form",
                    icon: "pi pi-file",
                    command: () => {
                      if (typeof window !== "undefined") {
                        window.dispatchEvent(new CustomEvent("resident-podiatrist:start-new-tracker"));
                      }
                    },
                  },
                  {
                    label: "Copy From Last Version",
                    icon: "pi pi-clone",
                    command: () => {
                      if (typeof window !== "undefined") {
                        window.dispatchEvent(new CustomEvent("resident-podiatrist:copy-last-tracker"));
                      }
                    },
                  },
                ]}
                icon="pi pi-chevron-down"
                severity="warning"
                rounded
                outlined
                className="p-button-sm"
                loading={podiatristActionLoading["start-new-tracker"] === true}
              />
              <Button
                icon="pi pi-sparkles"
                rounded
                label="Generate Forms from CarePlan"
                severity="warning"
                size="small"
                loading={podiatristActionLoading["generate-careplan"] === true}
                disabled={podiatristActionLoading["start-new-tracker"] === true}
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("resident-podiatrist:generate-forms"));
                  }
                }}
              />
            </div>
          ) : null}
          {isProviderResidentAdlRoute && residentId ? (
            <div className="app-breadcrumbs-actions">
              <ActionMenuButton
                label="Add"
                model={residentAdlActionItems}
                icon="pi pi-chevron-down"
                severity="warning"
                rounded
                className="p-button-sm"
              />
              <Button
                icon="pi pi-file-export"
                rounded
                label="Download Report"
                severity="warning"
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("resident-adl:request-download-report"));
                  }
                }}
              />
            </div>
          ) : null}
          {isBinderRoute ? (
            <div className="app-breadcrumbs-actions">
              <Button
                icon="pi pi-plus"
                rounded
                label="Add Folder"
                severity="warning"
                size="small"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("binder:open-add-folder"));
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AppBreadcrumbs;
